import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  PlusIcon,
  TrashIcon,
  ArrowPathIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  NoSymbolIcon,
  GlobeAltIcon,
  DevicePhoneMobileIcon,
  ServerIcon,
  ComputerDesktopIcon,
  EnvelopeIcon,
  PencilIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import AdminLayout from '../components/AdminLayout';
import Pagination from '../components/Pagination';
import toast from 'react-hot-toast';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

// Entity type icons and labels
const ENTITY_TYPES = {
  ip: { icon: GlobeAltIcon, label: 'IP Address', placeholder: '192.168.1.1' },
  ip_range: { icon: ServerIcon, label: 'IP Range (CIDR)', placeholder: '192.168.1.0/24' },
  fingerprint: { icon: DevicePhoneMobileIcon, label: 'Fingerprint', placeholder: 'fingerprint hash' },
  asn: { icon: ComputerDesktopIcon, label: 'ASN', placeholder: 'AS12345' },
  user_agent_pattern: { icon: ComputerDesktopIcon, label: 'User Agent Pattern', placeholder: 'regex pattern' },
  email_domain: { icon: EnvelopeIcon, label: 'Email Domain', placeholder: 'example.com' }
};

// Add/Edit Modal Component
const EntityModal = ({ isOpen, onClose, onSave, entity = null }) => {
  const [formData, setFormData] = useState({
    entityType: entity?.entity_type || 'ip',
    entityValue: entity?.entity_value || '',
    reason: entity?.block_reason || '',
    expiresAt: entity?.expires_at ? new Date(entity.expires_at).toISOString().slice(0, 16) : ''
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.entityValue || !formData.reason) {
      toast.error('Value and reason are required');
      return;
    }
    
    setSaving(true);
    try {
      await onSave({
        entityType: formData.entityType,
        entityValue: formData.entityValue,
        reason: formData.reason,
        expiresAt: formData.expiresAt || null
      });
      onClose();
    } catch (error) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  const typeConfig = ENTITY_TYPES[formData.entityType];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900">
            {entity ? 'Edit Blocked Entity' : 'Block New Entity'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Entity Type</label>
            <select
              value={formData.entityType}
              onChange={(e) => setFormData({ ...formData, entityType: e.target.value })}
              disabled={!!entity}
              className="w-full border border-gray-300 rounded-lg p-2 disabled:bg-gray-100"
            >
              {Object.entries(ENTITY_TYPES).map(([key, { label }]) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Value</label>
            <input
              type="text"
              value={formData.entityValue}
              onChange={(e) => setFormData({ ...formData, entityValue: e.target.value })}
              placeholder={typeConfig?.placeholder || 'Enter value'}
              disabled={!!entity}
              className="w-full border border-gray-300 rounded-lg p-2 disabled:bg-gray-100"
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Reason</label>
            <textarea
              value={formData.reason}
              onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
              placeholder="Why is this entity being blocked?"
              className="w-full border border-gray-300 rounded-lg p-2 h-24 resize-none"
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Expires At (optional)
            </label>
            <input
              type="datetime-local"
              value={formData.expiresAt}
              onChange={(e) => setFormData({ ...formData, expiresAt: e.target.value })}
              className="w-full border border-gray-300 rounded-lg p-2"
            />
            <p className="text-xs text-gray-500 mt-1">Leave empty for permanent block</p>
          </div>
          
          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
            >
              {saving ? 'Saving...' : (entity ? 'Update' : 'Block Entity')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const BlockedEntities = () => {
  const [entities, setEntities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [entityType, setEntityType] = useState('');
  const [isActive, setIsActive] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [showFilters, setShowFilters] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingEntity, setEditingEntity] = useState(null);

  const fetchEntities = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage,
        limit: 20,
        sortBy,
        sortOrder,
        ...(search && { search }),
        ...(entityType && { entityType }),
        ...(isActive && { isActive })
      });

      const response = await axios.get(`${API_BASE_URL}/api/admin/risk/blocked?${params}`);
      setEntities(response.data.data.entities);
      setTotalPages(response.data.data.pagination.totalPages);
    } catch (error) {
      console.error('Error fetching blocked entities:', error);
      toast.error('Failed to fetch blocked entities');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEntities();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, entityType, isActive, sortBy, sortOrder]);

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchEntities();
  };

  const handleAdd = async (data) => {
    await axios.post(`${API_BASE_URL}/api/admin/risk/blocked`, data);
    toast.success('Entity blocked successfully');
    fetchEntities();
  };

  const handleUpdate = async (id, data) => {
    await axios.put(`${API_BASE_URL}/api/admin/risk/blocked/${id}`, data);
    toast.success('Entity updated successfully');
    fetchEntities();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to remove this block?')) return;
    
    try {
      await axios.delete(`${API_BASE_URL}/api/admin/risk/blocked/${id}`);
      toast.success('Block removed successfully');
      fetchEntities();
    } catch (error) {
      console.error('Error removing block:', error);
      toast.error('Failed to remove block');
    }
  };

  const handleToggleActive = async (entity) => {
    try {
      await axios.put(`${API_BASE_URL}/api/admin/risk/blocked/${entity.id}`, {
        isActive: !entity.is_active
      });
      toast.success(entity.is_active ? 'Block disabled' : 'Block enabled');
      fetchEntities();
    } catch (error) {
      console.error('Error toggling block:', error);
      toast.error('Failed to update block');
    }
  };

  const EntityIcon = ({ type }) => {
    const config = ENTITY_TYPES[type];
    const Icon = config?.icon || NoSymbolIcon;
    return <Icon className="w-5 h-5" />;
  };

  return (
    <AdminLayout>
      <div>
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Blocked Entities</h1>
            <p className="text-gray-600 mt-1">Manage blocked IPs, fingerprints, and other entities</p>
          </div>
          <div className="flex items-center gap-3">
            <Link
              to="/risk"
              className="px-4 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg"
            >
              ← Back to Dashboard
            </Link>
            <button
              onClick={() => { setEditingEntity(null); setShowModal(true); }}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-2"
            >
              <PlusIcon className="w-5 h-5" />
              Block New
            </button>
            <button
              onClick={fetchEntities}
              disabled={loading}
              className="p-2 text-gray-600 hover:text-primary-600 hover:bg-gray-100 rounded-lg"
            >
              <ArrowPathIcon className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <div className="flex flex-wrap items-center gap-4">
            <form onSubmit={handleSearch} className="flex-1 min-w-64">
              <div className="relative">
                <MagnifyingGlassIcon className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by value or reason..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg"
                />
              </div>
            </form>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`px-4 py-2 rounded-lg border ${showFilters ? 'bg-primary-50 border-primary-300' : 'border-gray-300'}`}
            >
              <FunnelIcon className="w-5 h-5 inline mr-2" />
              Filters
            </button>
          </div>

          {showFilters && (
            <div className="mt-4 pt-4 border-t grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Entity Type</label>
                <select
                  value={entityType}
                  onChange={(e) => { setEntityType(e.target.value); setCurrentPage(1); }}
                  className="w-full border border-gray-300 rounded-lg p-2"
                >
                  <option value="">All Types</option>
                  {Object.entries(ENTITY_TYPES).map(([key, { label }]) => (
                    <option key={key} value={key}>{label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  value={isActive}
                  onChange={(e) => { setIsActive(e.target.value); setCurrentPage(1); }}
                  className="w-full border border-gray-300 rounded-lg p-2"
                >
                  <option value="">All</option>
                  <option value="true">Active Only</option>
                  <option value="false">Inactive Only</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Sort By</label>
                <select
                  value={`${sortBy}-${sortOrder}`}
                  onChange={(e) => {
                    const [field, order] = e.target.value.split('-');
                    setSortBy(field);
                    setSortOrder(order);
                    setCurrentPage(1);
                  }}
                  className="w-full border border-gray-300 rounded-lg p-2"
                >
                  <option value="created_at-desc">Newest First</option>
                  <option value="created_at-asc">Oldest First</option>
                  <option value="hit_count-desc">Most Hits</option>
                  <option value="hit_count-asc">Least Hits</option>
                </select>
              </div>
            </div>
          )}
        </div>

        {/* Entities Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Value</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reason</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Hits</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Expires</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="8" className="px-4 py-8 text-center">
                    <div className="flex justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                    </div>
                  </td>
                </tr>
              ) : entities.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-4 py-8 text-center text-gray-500">
                    No blocked entities found
                  </td>
                </tr>
              ) : (
                entities.map((entity) => (
                  <tr key={entity.id} className={`hover:bg-gray-50 ${!entity.is_active ? 'opacity-50' : ''}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <EntityIcon type={entity.entity_type} />
                        <span className="text-sm capitalize">{entity.entity_type.replace(/_/g, ' ')}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <code className="text-sm bg-gray-100 px-2 py-1 rounded max-w-xs truncate block">
                        {entity.entity_value}
                      </code>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 max-w-xs truncate">
                      {entity.block_reason}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-sm font-medium ${entity.hit_count > 0 ? 'text-red-600' : 'text-gray-400'}`}>
                        {entity.hit_count || 0}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => handleToggleActive(entity)}
                        className={`px-2 py-1 text-xs rounded ${
                          entity.is_active
                            ? 'bg-green-100 text-green-700'
                            : 'bg-gray-100 text-gray-500'
                        }`}
                      >
                        {entity.is_active ? 'Active' : 'Inactive'}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {entity.expires_at
                        ? new Date(entity.expires_at).toLocaleDateString()
                        : 'Never'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {new Date(entity.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => { setEditingEntity(entity); setShowModal(true); }}
                          className="p-1 text-gray-400 hover:text-gray-600"
                          title="Edit"
                        >
                          <PencilIcon className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(entity.id)}
                          className="p-1 text-gray-400 hover:text-red-600"
                          title="Delete"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-4">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          </div>
        )}

        {/* Add/Edit Modal */}
        <EntityModal
          isOpen={showModal}
          onClose={() => { setShowModal(false); setEditingEntity(null); }}
          onSave={editingEntity ? (data) => handleUpdate(editingEntity.id, data) : handleAdd}
          entity={editingEntity}
        />
      </div>
    </AdminLayout>
  );
};

export default BlockedEntities;
